import { useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { DEVICE_ACTIONS } from "components/Actions/types/enums";
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import Dialog from "components/DesignComponents/Dialog";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { maxDialogWidth } from "shared/styles";
import { TASK_TYPES } from "shared/types/enums";

import TaskAttributes from "./TaskAttributes";
import TaskFileData from "./TaskFileData";
import TaskGeneralData from "./TaskGeneralData";
import {
    MainAttributes,
    TASK_ATTRIBUTES,
    TaskDetailsProps,
    TxFwBundleData
} from "./types";

const TaskDetails = ({
    data,
    isOpen,
    setTaskData,
    close
}: TaskDetailsProps) => {
    const { t } = useTranslation();
    const { toMd, fromMd } = useMediaQueries();

    const [mainAttributes, setMainAttributes] = useState<MainAttributes | null>(
        null
    );

    const [txFwBundleFileData, setTxFwBundleFileData] =
        useState<Partial<TxFwBundleData> | null>(null);

    const getMainAttributesAndUpdateTxFwBundleData = (
        parsedData: any,
        txFwBundleData: Partial<TxFwBundleData>
    ) => {
        const entries = Object.entries(parsedData) as MainAttributes;

        const attributesToDelete = [
            TASK_ATTRIBUTES.Price,
            TASK_ATTRIBUTES.Message,
            TASK_ATTRIBUTES.Password
        ];

        const txFwBundleFileAttributes = [
            TASK_ATTRIBUTES.FileSizeBytes,
            TASK_ATTRIBUTES.IncludedFileIds,
            TASK_ATTRIBUTES.SameGradeFileIds
        ];

        return entries.filter(([attributeName, value]) => {
            if (attributesToDelete.includes(attributeName)) {
                return false;
            }

            if (txFwBundleFileAttributes.includes(attributeName)) {
                txFwBundleData[attributeName as keyof TxFwBundleData] =
                    value as never;

                return false;
            }

            return true;
        });
    };

    const distributeAttributes = () => {
        if (data?.attributes) {
            try {
                const parsedData = JSON.parse(data.attributes);
                const txFwBundleData: Partial<TxFwBundleData> = {};

                const mainAttributeEntries =
                    getMainAttributesAndUpdateTxFwBundleData(
                        parsedData,
                        txFwBundleData
                    );

                if (mainAttributeEntries.length > 0) {
                    setMainAttributes(mainAttributeEntries);
                }

                if (Object.keys(txFwBundleData).length > 0) {
                    setTxFwBundleFileData(txFwBundleData);
                }
            } catch {
                setMainAttributes(null);
            }
        }
    };

    const handleCloseDialog = () => close(DEVICE_ACTIONS.TaskDetails);

    const taskType: TASK_TYPES | undefined = data?.type;
    const taskTypeName = taskType ? t(`General##${taskType}`) : "";

    return (
        <Dialog
            data-testid="task-details-dialog"
            isTitleSeparator
            isActionsSeparator
            title={taskTypeName}
            open={isOpen}
            close={handleCloseDialog}
            actions={
                <Button
                    fullWidth
                    color={BUTTON_COLORS.Secondary}
                    size={BUTTON_SIZES.Normal}
                    variant={BUTTON_VARIANTS.TextOnly}
                    onClick={handleCloseDialog}
                >
                    {t("Button##close")}
                </Button>
            }
            slotProps={{
                transition: {
                    onExited: () => {
                        setTaskData(null);
                        setMainAttributes(null);
                        setTxFwBundleFileData(null);
                    },
                    onEntering: () => {
                        distributeAttributes();
                    }
                }
            }}
            disableEnforceFocus
            disableRestoreFocus
            extendToMaxWidth={toMd}
            css={css(fromMd && maxDialogWidth)}
        >
            <div
                css={css({
                    display: "grid",
                    gridTemplateColumns: "30% 1fr",
                    gridColumnGap: "24px",
                    gridRowGap: "4px",

                    "& > h4": {
                        fontWeight: "600",
                        letterSpacing: "0.15px",
                        lineHeight: "20px",
                        gridColumn: "1/-1",
                        marginBottom: "4px"
                    },

                    "& > span + h4": {
                        marginTop: "20px"
                    }
                })}
            >
                {mainAttributes && (
                    <TaskAttributes
                        separateAttributes={mainAttributes}
                        taskType={taskType}
                    />
                )}

                <TaskGeneralData data={data} />

                <TaskFileData
                    generalData={{ id: data?.file_id, file: data?.file }}
                    txFwBundleData={txFwBundleFileData}
                />
            </div>
        </Dialog>
    );
};

export default TaskDetails;
